import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { store } from './lib/context/store'
import './index.css'
/* GH #82 — leaflet's stylesheet used to be imported here, so every visitor
   downloaded it whether or not they ever opened a map. Only PartnerMapView uses
   react-leaflet, so the import moved there and now travels with that chunk. */
import App from './App.tsx'

/**
 * Take the splash down — but only once the app is actually on screen.
 *
 * THE BUG THIS FIXES. The first version called this two animation frames after
 * `render()` was invoked. That is not the same as the app having painted:
 * React 18 renders concurrently, so `render()` returns before the commit. On a
 * cold load the splash was therefore removed while #root was still empty,
 * giving a flash of blank white between the splash disappearing and the app
 * appearing — the "everything vanishes, then instantly comes back" effect.
 *
 * Waiting for #root to actually have content removes the gap entirely: the app
 * is painted behind the splash before the splash starts to fade.
 *
 * MINIMUM DISPLAY TIME. On a warm cache the app mounts in well under 100ms, and
 * a splash that appears and vanishes inside one blink reads as a flicker —
 * worse than no splash at all. 450ms is long enough to read as a deliberate
 * transition and short enough that nobody waits for it. On a cold load the
 * bundle takes longer than that anyway, so it costs nothing where it matters.
 *
 * SAFETY VALVE. If the app never mounts — a thrown error during render, a
 * failed chunk — the splash must not sit there forever hiding whatever the
 * error boundary rendered. After 8 seconds it goes regardless.
 */
const SPLASH_MIN_MS = 450;
const SPLASH_MAX_MS = 8000;
const splashStart = performance.now();

function removeSplash(): void {
  const el = document.getElementById('lc-splash');
  if (!el) return;
  const wait = Math.max(0, SPLASH_MIN_MS - (performance.now() - splashStart));
  window.setTimeout(() => {
    el.classList.add('lc-splash-out');
    /* Removed, not just faded — it covers the viewport and would otherwise
       keep swallowing clicks. The delay matches the CSS transition. */
    window.setTimeout(() => el.remove(), 360);
  }, wait);
}

function whenAppPainted(done: () => void): void {
  const root = document.getElementById('root');
  if (!root) { done(); return; }

  /* Already has content — nothing to wait for. */
  if (root.childElementCount > 0) { done(); return; }

  const observer = new MutationObserver(() => {
    if (root.childElementCount > 0) {
      observer.disconnect();
      /* One more frame so the browser has actually painted the children,
         rather than merely having them in the DOM. */
      requestAnimationFrame(() => requestAnimationFrame(done));
    }
  });
  observer.observe(root, { childList: true });

  window.setTimeout(() => { observer.disconnect(); done(); }, SPLASH_MAX_MS);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Acting context — which company and which side of the marketplace the
        user is working as. One slice; see lib/context/store.ts for why Redux
        is here at all when the rest of the app uses React context. */}
    <Provider store={store}>
      <App />
    </Provider>
  </StrictMode>,
)

whenAppPainted(removeSplash);
